/** @jsxImportSource @emotion/react */

import { createContext, FC, useState } from "react";

type NotificationDetailBaseType = {
  key?: string;
  title?: any;
  message: any;
  timeout?: number;
  autoClose?: boolean;
  onClick?: (remove?: boolean) => void;
  appBar?: boolean;
  notification?: boolean;
  hideClose?: boolean;
};

export type NotificationDetailType = NotificationDetailBaseType & {
  type: NotificationType;
};

export type NotificationType = "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "DESTROY";

type NotificationCallbackType = (detail: NotificationDetailBaseType) => void;
type NotificationDestroyType = () => void;

type NotificationContextType = {
  success: NotificationCallbackType;
  error: NotificationCallbackType;
  warning: NotificationCallbackType;
  info: NotificationCallbackType;
  destroy: NotificationDestroyType;
  items: NotificationDetailType[];
};

type NotificationProviderPropType = {
  onNotify: NotificationCallbackType;
  onDestroy: NotificationDestroyType;
};

const NotificationContext = createContext({} as NotificationContextType);

const NotificationProvider: FC<NotificationProviderPropType> = ({ children, onNotify, onDestroy }) => {
  let [items, setItems] = useState<NotificationDetailType[]>([]);

  const handleNotify = (type: NotificationType) => {
    return (detail: NotificationDetailBaseType) => {
      const notificationDetail = enrichNotificationDetail(detail, type);

      onNotify(notificationDetail);
      addNotificationItem(notificationDetail);
    };
  };

  const handleDestroy = () => {
    return () => {
      if (onDestroy) {
        onDestroy();
      }
    };
  };

  function addNotificationItem(detail: NotificationDetailType) {
    if (!detail.appBar) {
      return;
    }
    setItems((items) => {
      const shouldNotify = items.find((item) => item.key === detail.key) === undefined;
      return shouldNotify ? [...items, detail] : items;
    });
  }

  function removeNotificationItem(key: string) {
    setItems((items) => [...items].filter((item) => item.key !== key));
  }

  function enrichNotificationDetail(
    detail: NotificationDetailBaseType,
    type: NotificationType
  ): NotificationDetailType {
    const key = detail.key ?? detail.message;
    const onClick = (remove: boolean = false) => {
      remove && removeNotificationItem(key);
      detail.onClick();
    };
    return { ...detail, key, type, onClick } as NotificationDetailType;
  }

  return (
    <NotificationContext.Provider
      value={{
        info: handleNotify("INFO"),
        success: handleNotify("SUCCESS"),
        error: handleNotify("ERROR"),
        warning: handleNotify("WARNING"),
        destroy: handleDestroy(),
        items,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export { NotificationContext };

export default NotificationProvider;
